import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTheme, api } from "../App";
import {
  LayoutDashboard,
  MessageSquare,
  Zap,
  FlaskConical,
  Users,
  MessageCircle,
  BarChart3,
  Link2,
  Settings,
  Moon,
  Sun,
  Wand2,
  Brain,
  CreditCard,
  Sparkles,
  Image as ImageIcon,
  Bell,
  LifeBuoy,
  Handshake,
  UserSquare2,
  Package,
} from "lucide-react";
import { ScrollArea } from "./ui/scroll-area";
import { Separator } from "./ui/separator";
import SidebarInstagramIdentity from "./SidebarInstagramIdentity";
import { useSupport } from "../context/SupportContext";
import { prefetchHandlers } from "../lib/routePrefetch";

// Bug #7 (Apr 2026) — exact order requested by user. ``Trends`` is
// preserved at the end since the user said "do not add or remove
// items, only reorder".
import { isComingSoon } from "../config/comingSoon";

const mainNavItems = [
  { path: "/dashboard",    icon: LayoutDashboard, label: "Dashboard" },
  { path: "/posts",        icon: ImageIcon,       label: "Posts & Reels" },
  { path: "/automation",   icon: Zap,             label: "Automation" },
  { path: "/contacts",     icon: UserSquare2,     label: "Contacts", isNew: true },
  { path: "/products",     icon: Package,         label: "Affiliate Products", isNew: true },
  { path: "/bio",          icon: Link2,           label: "Link-in-Bio" },
  { path: "/analytics",    icon: BarChart3,       label: "Analytics" },
  { path: "/collabs",      icon: Handshake,       label: "Marketplace", isNew: true },
  { path: "/leads",        icon: Users,           label: "Leads" },
  { path: "/ab-testing",   icon: FlaskConical,    label: "A/B Testing" },
  { path: "/ai-writer",    icon: Wand2,           label: "AI Writer" },
  { path: "/growth-agent", icon: Brain,           label: "Growth Agent" },
  { path: "/trends",       icon: Sparkles,        label: "Trends", isNew: true },
];

function NotifBell() {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  useEffect(() => {
    api.get("/notif/unread-count").then(({ data }) => setCount(data.unread_count || 0)).catch(() => {});
    const interval = setInterval(() => {
      api.get("/notif/unread-count").then(({ data }) => setCount(data.unread_count || 0)).catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <button onClick={() => navigate("/notifications")} className="relative p-2 rounded-lg hover:bg-secondary/60 transition-colors" data-testid="bell-icon">
      <Bell className="w-[18px] h-[18px] text-muted-foreground" />
      {count > 0 && (
        <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 flex items-center justify-center text-[9px] font-bold text-white bg-red-500 rounded-full min-w-[18px] px-1">{count > 9 ? "9+" : count}</span>
      )}
    </button>
  );
}

export default function DesktopSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { openSupport } = useSupport();
  const [planName, setPlanName] = useState("");

  useEffect(() => {
    api.get("/sub/usage").then(({ data }) => setPlanName(data?.plan_name || "")).catch(() => {});
  }, []);

  const NavItem = ({ item }) => {
    const isActive = location.pathname === item.path ||
      (item.path === "/automation" && location.pathname.startsWith("/automation"));

    // A locked feature renders as a DIV, not a disabled button. A
    // disabled button still occupies the tab order and still reads as
    // "control you may not use"; this reads as "entry that is not ready",
    // which is what it is. No onClick, no prefetch — prefetching the
    // chunk for a page nobody can open is pure waste.
    if (isComingSoon(item.path)) {
      return (
        <div
          className="relative w-full flex items-center gap-3 px-4 py-2 rounded-xl text-sm font-medium opacity-55 cursor-default select-none"
          data-testid={`sidebar-${item.label.toLowerCase().replace(/\s/g, '-')}-locked`}
          aria-disabled="true"
          title={`${item.label} — coming soon`}
        >
          <div className="relative z-10 flex items-center gap-3 text-muted-foreground">
            <item.icon className="w-5 h-5" />
            <span>{item.label}</span>
            <span className="px-1.5 py-0.5 text-[10px] font-bold bg-secondary text-muted-foreground rounded-full whitespace-nowrap">
              SOON
            </span>
          </div>
        </div>
      );
    }
    
    // prefetchHandlers starts the route's lazy-chunk download on hover/focus/
    // touch. Without it a click begins a serial chain — download chunk, mount
    // component, THEN fetch its data — and those legs cannot overlap because
    // the fetch lives inside the code being downloaded. See lib/routePrefetch.
    return (
      <button
        onClick={() => navigate(item.path)}
        {...prefetchHandlers(item.path)}
        className="relative w-full flex items-center gap-3 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 group"
        data-testid={`sidebar-${item.label.toLowerCase().replace(/\s/g, '-')}`}
        aria-label={item.label}
        aria-current={isActive ? "page" : undefined}
      >
        {isActive && (
          <motion.div
            layoutId="sidebar-active"
            className="absolute inset-0 bg-primary/10 rounded-xl"
            transition={{ type: "spring", stiffness: 400, damping: 30 }}
          />
        )}
        <div className={`relative z-10 flex items-center gap-3 ${
          isActive ? "text-primary" : "text-muted-foreground group-hover:text-foreground"
        }`}>
          <item.icon className="w-5 h-5" />
          <span>{item.label}</span>
          {item.isNew && (
            <span className="px-1.5 py-0.5 text-[10px] font-bold bg-gradient-to-r from-violet-500 to-pink-500 text-white rounded-full">NEW</span>
          )}
        </div>
        {isActive && (
          <motion.div
            layoutId="sidebar-indicator"
            className="absolute right-3 w-1.5 h-1.5 rounded-full bg-primary"
          />
        )}
      </button>
    );
  };

  return (
    <aside className="fixed left-0 top-0 bottom-0 w-64 border-r border-border/50 bg-background/80 backdrop-blur-xl flex flex-col" data-testid="desktop-sidebar" role="navigation" aria-label="Main navigation">
      {/* Logo + Header Icons */}
      <div className="p-5 border-b border-border/50 flex items-center justify-between">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
          <div className="w-10 h-10 rounded-xl overflow-hidden shadow-lg">
            <img src="/tadlo-logo.jpeg" alt="Tadlo" className="w-full h-full object-cover" />
          </div>
          <span className="text-xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit' }}>Tadlo</span>
        </button>
        <div className="flex items-center gap-0.5">
          <NotifBell />
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg hover:bg-secondary/60 transition-colors"
            data-testid="sidebar-theme-toggle"
            title={theme === "dark" ? "Light Mode" : "Dark Mode"}
          >
            {theme === "dark" ? <Sun className="w-[18px] h-[18px] text-muted-foreground" /> : <Moon className="w-[18px] h-[18px] text-muted-foreground" />}
          </button>
          <button
            onClick={() => navigate("/settings")}
            className={`p-2 rounded-lg transition-colors ${location.pathname === "/settings" ? "bg-primary/10 text-primary" : "hover:bg-secondary/60 text-muted-foreground"}`}
            data-testid="sidebar-settings"
            title="Settings"
          >
            <Settings className="w-[18px] h-[18px]" />
          </button>
        </div>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-1">
          {mainNavItems.map((item) => (
            <NavItem key={item.path} item={item} />
          ))}
        </nav>
      </ScrollArea>

      {/* Bottom Section */}
      <div className="p-3 border-t border-border/50 space-y-2">
        {/* Subscription with plan name */}
        <button
          onClick={() => navigate("/pricing")}
          className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
            location.pathname === "/pricing"
              ? "bg-primary/10 text-primary"
              : "text-muted-foreground hover:bg-secondary hover:text-foreground"
          }`}
          data-testid="sidebar-subscription"
        >
          <CreditCard className="w-5 h-5" />
          <span className="flex items-center gap-2">
            Subscription
            {planName && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-violet-500/10 text-violet-500">{planName}</span>}
          </span>
        </button>

        <Separator className="my-3" />

        {/* Help & Support — opens global SupportModal */}
        <button
          type="button"
          onClick={() => openSupport("report")}
          className="w-full flex items-center gap-3 px-4 py-2 rounded-xl text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-all"
          data-testid="sidebar-help-support"
        >
          <LifeBuoy className="w-5 h-5" />
          <span>Help &amp; Support</span>
        </button>

        {/* Instagram identity + account switcher + menu */}
        <SidebarInstagramIdentity />
      </div>
    </aside>
  );
}
